// The in-page stand-in for Telegram's native popup (`showPopup`) in a plain browser: one `<dialog>` with the message, a button
// per choice and a Cancel button. Plain DOM instead of a Vue component, since the helpers that call it are not part of a template.
import { i18n } from '@/i18n'

export interface WebChoice {
  id: string
  text: string
  destructive?: boolean
}

// Resolves to the chosen id, or null when cancelled (Cancel button, Esc, a click on the backdrop).
export function webChooseDialog(message: string, choices: WebChoice[]): Promise<string | null> {
  return new Promise((resolve) => {
    const dialog = document.createElement('dialog')
    dialog.className = 'web-dialog'
    dialog.style.cssText =
      'max-width:min(24rem,90vw);border:0;border-radius:0.75rem;padding:1rem;background:var(--tg-section-bg-color,#fff);color:var(--tg-text-color,#000)'

    const text = document.createElement('p')
    text.textContent = message
    text.style.cssText = 'margin:0 0 1rem;white-space:pre-wrap'
    dialog.appendChild(text)

    const buttons = document.createElement('div')
    buttons.style.cssText = 'display:flex;flex-direction:column;gap:0.5rem'
    dialog.appendChild(buttons)

    let result: string | null = null
    const addButton = (label: string, id: string | null, destructive = false): HTMLButtonElement => {
      const button = document.createElement('button')
      button.type = 'button'
      button.textContent = label
      button.style.cssText = `padding:0.6rem;border-radius:0.5rem;border:1px solid var(--tg-hint-color,#8884);background:transparent;font:inherit;cursor:pointer;color:${
        destructive ? 'var(--tg-destructive-text-color,#d33)' : 'inherit'
      }`
      button.addEventListener('click', () => {
        result = id
        dialog.close()
      })
      buttons.appendChild(button)
      return button
    }

    for (const choice of choices) addButton(choice.text, choice.id, choice.destructive)
    const cancel = addButton(i18n.global.t('common.cancel'), null)

    // (Esc fires `close` on its own, without a result, which stays null)
    dialog.addEventListener('close', () => {
      dialog.remove()
      resolve(result)
    })
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close()
    })

    document.body.appendChild(dialog)
    dialog.showModal()
    cancel.focus()
  })
}
